import {
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  type ReactNode,
} from 'react';
import { clsx } from 'clsx';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';

/**
 * Table primitives.
 *
 * `DataTable` composes these for anything server-paginated; they are exported on
 * their own for the small fixed tables — a vehicle's attributes, a role's permission
 * matrix — where a full data grid would be ceremony.
 */

export type TableDensity = 'comfortable' | 'compact';

export const DENSITY_CELL: Record<TableDensity, string> = {
  comfortable: 'px-3 py-3',
  compact: 'px-3 py-2',
};

export function TableWrap({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx('relative w-full overflow-x-auto overscroll-x-contain', className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Table({ className, children, ...rest }: HTMLAttributes<HTMLTableElement>) {
  return (
    <table
      className={clsx('w-full border-collapse text-left text-micro', className)}
      {...rest}
    >
      {children}
    </table>
  );
}

export function THead({
  sticky = true,
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement> & { sticky?: boolean }) {
  return (
    <thead
      className={clsx(
        'bg-surface-2',
        // The header has to survive being scrolled under, so it carries its own
        // background and a bottom rule rather than relying on the row beneath.
        sticky && 'sticky top-0 z-10',
        className,
      )}
      {...rest}
    >
      {children}
    </thead>
  );
}

/** Forwards its ref so a table can move keyboard focus between its own rows. */
export const TBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  function TBody({ className, children, ...rest }, ref) {
    return (
      <tbody ref={ref} className={clsx('divide-y divide-line', className)} {...rest}>
        {children}
      </tbody>
    );
  },
);

export function TFoot({ className, children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tfoot className={clsx('border-t border-line bg-surface-2 font-medium', className)} {...rest}>
      {children}
    </tfoot>
  );
}

export interface TrProps extends HTMLAttributes<HTMLTableRowElement> {
  clickable?: boolean;
  selected?: boolean;
}

export function Tr({ clickable = false, selected = false, className, children, ...rest }: TrProps) {
  return (
    <tr
      className={clsx(
        'transition-colors duration-100',
        clickable && 'cursor-pointer hover:bg-surface-2',
        selected && 'bg-accent-soft',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

export type SortDirection = 'asc' | 'desc';

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Right-align numeric columns so the digits stack. */
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  /** The direction currently applied to this column, if any. */
  sorted?: SortDirection | null;
  onSort?: () => void;
  density?: TableDensity;
  numeric?: boolean;
}

export function Th({
  align = 'left',
  sortable = false,
  sorted = null,
  onSort,
  density = 'comfortable',
  numeric = false,
  className,
  children,
  ...rest
}: ThProps) {
  const content = (
    <span
      className={clsx(
        'inline-flex items-center gap-1',
        align === 'right' && 'flex-row-reverse',
        align === 'center' && 'justify-center',
      )}
    >
      <span className="truncate">{children}</span>
      {sortable ? (
        sorted === 'asc' ? (
          <ArrowUp className="h-3 w-3 shrink-0 text-accent" aria-hidden />
        ) : sorted === 'desc' ? (
          <ArrowDown className="h-3 w-3 shrink-0 text-accent" aria-hidden />
        ) : (
          <ChevronsUpDown
            className="h-3 w-3 shrink-0 text-faint opacity-0 transition-opacity group-hover/th:opacity-100"
            aria-hidden
          />
        )
      ) : null}
    </span>
  );

  return (
    <th
      scope="col"
      aria-sort={sortable ? (sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none') : undefined}
      className={clsx(
        'group/th border-b border-line text-label font-semibold tracking-wide text-muted uppercase whitespace-nowrap',
        DENSITY_CELL[density],
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        numeric && 'tabular-nums',
        sortable && 'cursor-pointer select-none hover:text-ink',
        className,
      )}
      onClick={sortable ? onSort : undefined}
      onKeyDown={
        sortable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSort?.();
              }
            }
          : undefined
      }
      tabIndex={sortable ? 0 : undefined}
      {...rest}
    >
      {content}
    </th>
  );
}

export interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'right' | 'center';
  density?: TableDensity;
  numeric?: boolean;
  muted?: boolean;
  /** Stops a long value from stretching the column. */
  truncate?: boolean;
}

export function Td({
  align = 'left',
  density = 'comfortable',
  numeric = false,
  muted = false,
  truncate = false,
  className,
  children,
  ...rest
}: TdProps) {
  return (
    <td
      className={clsx(
        // One row, one line. A wrapping cell turns a scannable column of
        // figures into a ragged block three times its height, and the table is
        // inside its own scroller for the widths that cannot hold it.
        'align-middle whitespace-nowrap text-ink',
        DENSITY_CELL[density],
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        numeric && 'font-data tabular-nums',
        muted && 'text-muted',
        truncate && 'max-w-[16rem] truncate',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

/** A row of label/value pairs — a vehicle's attributes, a party's contact details. */
export function DefinitionList({
  items,
  columns = 2,
  className,
}: {
  items: Array<{ label: ReactNode; value: ReactNode }>;
  columns?: 1 | 2 | 3;
  className?: string;
}) {
  return (
    <dl
      className={clsx(
        'grid gap-x-6 gap-y-3',
        columns === 1 && 'grid-cols-1',
        columns === 2 && 'grid-cols-1 sm:grid-cols-2',
        columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        className,
      )}
    >
      {items.map((item, index) => (
        <div key={index} className="min-w-0">
          <dt className="text-label font-medium tracking-wide text-faint uppercase">
            {item.label}
          </dt>
          <dd className="mt-0.5 min-w-0 text-micro text-ink">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
